"use client";

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Collapse } from "@mui/material";
import BasicTable from "../../../Components/Table/Material";
import useApi from "../../../Hooks/useApi";
import { usePagination } from "../../../Hooks/usePagination";

import { handleApiError } from "../../../Helpers/ErrorHandler";
import Common from "../../../Components/Layouts/Common.js";
import ButtonToolbar from "../../Shared/ButtonToolbar.js";
import showToast from "../../../Helpers/toast.js";
import ConfirmModal from "../../../Components/Modal/ConfirmModal.js";
import TextField from "../../../Components/UI/Inputs/TextField.js";
import DropDown from "../../../Components/UI/Inputs/DropDown.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faFilter } from "@fortawesome/free-solid-svg-icons";
import { sortRows, nextSortConfig } from "../../../Helpers/tableSort.js";

const STATUS_OPTIONS = [
  { name: "All", value: "" },
  { name: "Pending", value: "PENDING" },
  { name: "Completed", value: "COMPLETED" },
];

const ActionButtonPanel = ({ row, onDelete }) => {
  const [isModalOpen, setModalOpen] = useState(false);

  return (
    <div className="inline-flex flex-wrap gap-2 justify-center text-xs">
      {row.status === "COMPLETED" ? (
        <Link
          to={`/create-survey-questionnaire`}
          state={{
            name: row.name,
            id: row.id,
            isTemplate: true,
          }}
        >
          <button
            className={`bg-[#37a45be6] border border-gray-300 w-[150px] p-2 text-white rounded-md flex items-center justify-center`}
          >
            Use Template
          </button>
        </Link>
      ) : (
        <Link
          to={`/create-survey-questionnaire`}
          state={{ name: row.name, id: row.id }}
        >
          <button
            className={`bg-[#1976d2] border border-gray-300 w-[150px] p-2 text-white rounded-md flex items-center justify-center`}
          >
            Complete Draft
          </button>
        </Link>
      )}

      <Link
        to={"/view-survey-questionnaire"}
        state={{ id: row.id, viewType: "view" }}
      >
        <button
          className={`bg-[#1976d2] border border-gray-300 w-[150px] p-2 text-white rounded-md flex items-center justify-center`}
        >
          View Questionnaire
        </button>
      </Link>
      <Link /* to={`/mapped-survey`} */>
        <button
          disabled={true}
          className={`disabled:bg-gray-400 disabled:cursor-not-allowed bg-[#1976d2] border border-gray-300 w-[150px] p-2 text-white rounded-md flex items-center justify-center`}
        >
          Mapped Survey
        </button>
      </Link>

      <button
        // onClick={onDelete}
        onClick={() => {
          setModalOpen(true);
        }}
        className={`bg-red-500 border border-gray-300 w-[150px] p-2 text-white rounded-md flex items-center justify-center`}
      >
        Delete Questionnaire
      </button>
      <ConfirmModal
        label={
          <p>
            Are you sure,
            <br /> You want to Delete?
          </p>
        }
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        onConfirm={() => {
          onDelete();
          setModalOpen(false);
        }}
      />
    </div>
  );
};

const SurveyQuestionnaireList = () => {
  const { request } = useApi();
  const paginateProps = usePagination();

  const [questionnaireData, setQuestionnaireData] = useState();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const fetchSurveyQuestionnaire = async () => {
    try {
      const params = new URLSearchParams({
        offset: paginateProps.page,
        limit: paginateProps.rowsPerPage,
      });
      if (statusFilter) params.append("status", statusFilter);

      const response = await request(
        "GET",
        `/v1/survey/questionnaires/list?${params.toString()}`,
        {},
        "survey_service"
      );

      setQuestionnaireData(
        response?.content?.map((item) => ({
          ...item,
          status: item?.status === "COMPLETED" ? item.status : "PENDING",
        }))
      );
      paginateProps.setTotalData(response.totalElements);
    } catch (error) {
      console.log(error);
      handleApiError(error);
    }
  };

  useEffect(() => {
    fetchSurveyQuestionnaire();
  }, [paginateProps.page, paginateProps.rowsPerPage, statusFilter]);

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    paginateProps.setPage(0);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => nextSortConfig(prev, key));
  };

  const displayData = useMemo(() => {
    let rows = questionnaireData || [];
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      rows = rows.filter((row) => row.name?.toLowerCase().includes(term));
    }
    return sortRows(rows, sortConfig);
  }, [questionnaireData, searchTerm, sortConfig]);

  const columns = [
    {
      id: "name",
      label: "Questionnaire Title",
      minWidth: 170,
      sortable: true,
    },
    {
      id: "creationDate",
      label: "Date Created",
      minWidth: 100,
      sortable: true,
      // renderCell: (row) => {
      //   return new Date(row.creationDate)?.toLocaleDateString();
      // },
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 170,
      align: "center",
      renderCell: (row) => {
        return (
          <ActionButtonPanel row={row} onDelete={() => handleDelete(row)} />
        );
      },
    },
  ];

  const handleDelete = async (row) => {
    const questionnaireId = row?.id;

    if (!questionnaireId) return;

    try {
      const response = await request(
        "DELETE",
        `/v1/survey/questionnaires/${questionnaireId}`,
        {},
        "survey_service"
      );
      showToast({ message: response?.message });
      fetchSurveyQuestionnaire();
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className=" bg-white  relative h-full">
      <Common headline={" Survey Questionnaire List"}>
        <ButtonToolbar
          title={"Add New Survey Questionnaire"}
          navigateURL={"/select-survey-questionnaire-method"}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faFilter} />}
            onClick={() => setIsFilterVisible((prev) => !prev)}
          >
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          {(searchTerm || statusFilter) && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FontAwesomeIcon icon={faXmark} />}
              onClick={() => {
                setSearchTerm("");
                handleStatusFilterChange("");
              }}
            >
              Reset Filters
            </Button>
          )}
        </div>

        <Collapse in={isFilterVisible}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-4">
            <TextField
              value={searchTerm}
              name="searchTerm"
              onValueChange={(_, value) => setSearchTerm(value)}
              icon={<FontAwesomeIcon icon={faSearch} />}
              placeholder="Search questionnaire title (current page)"
              fullWidth
            />
            <DropDown
              setter={handleStatusFilterChange}
              placeholder="Select Status"
              value={statusFilter}
              options={STATUS_OPTIONS}
              fullWidth
            />
          </div>
        </Collapse>

        <BasicTable
          columns={columns}
          data={displayData}
          paginateProps={paginateProps}
          sortConfig={sortConfig}
          onSort={handleSort}
          maxHeight={15000}
        />
      </Common>
    </div>
  );
};

export default SurveyQuestionnaireList;
